import type {DiffAnchor} from '@github-ui/diffs/types'
import type {Direction} from '@primer/behaviors'
import {useFocusZone} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {GridNavigator} from '../helpers/grid-navigator'
import {useFocusNextCellAfterExpand} from './use-focus-next-cell-after-expand'
import {GRID_NAV_KEYS, KEY_TO_BIT} from '../constants'
import type {ClientDiffLine, DiffOrientation} from '../types'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import type {DiffLinesSlice} from '../stores/slices/diff-lines'
import {handleLineSelectionEffects} from '../helpers/line-selection'
import {goBack, goForward} from '@github-ui/history'

/**
 * Given a container, add a focusout hook that will call the provided callback when the focus leaves the container.
 */
// Disabling this function until we determine how to better reset a grid cell focus position when users leaves the grid.
// function useFocusOutGridNavigationBehavior(containerRef: React.RefObject<HTMLElement>, onFocusOut: () => void) {
//   const focusOutHandler = useCallback(
//     (event: FocusEvent) => {
//       // only trigger focusout behavior if the focus is leaving the container
//       if (!containerRef.current?.contains(event.relatedTarget as Node)) onFocusOut()
//     },
//     [containerRef, onFocusOut],
//   )

//   useEffect(() => {
//     const containerElement = containerRef.current
//     containerElement?.addEventListener('focusout', focusOutHandler)
//     return () => containerElement?.removeEventListener('focusout', focusOutHandler)
//   }, [containerRef, focusOutHandler])
// }

/**
 * Keeps the grid navigator's focusedGridCell property in sync with the document's focused element
 */
function useSyncedGridFocus(containerRef: React.RefObject<HTMLElement | null>, gridNavigator: GridNavigator) {
  const handleGridFocusIn = useCallback(
    (event: FocusEvent) => {
      const focusedCell = event.target as HTMLElement
      const gridCellId = focusedCell.getAttribute('data-grid-cell-id')
      if (gridCellId) gridNavigator.focusGridCell(gridCellId)
    },
    [gridNavigator],
  )

  useEffect(() => {
    const containerElement = containerRef.current
    containerElement?.addEventListener('focusin', handleGridFocusIn)
    return () => containerElement?.removeEventListener('focusin', handleGridFocusIn)
  }, [containerRef, handleGridFocusIn])
}

export function isGridNavigationKey(key: string) {
  if (!(key in KEY_TO_BIT)) return false
  const keyBit = KEY_TO_BIT[key as keyof typeof KEY_TO_BIT]
  return (keyBit & GRID_NAV_KEYS) > 0
}

export function useGridNavigation({
  addLineToSelection,
  clearSelectedLines,
  containerRef,
  fileAnchor,
  getfirstLineSelectionOnDrag,
  isSplitDiff,
  leftLines,
  rightLines,
  selectMultipleLines,
  disabled,
}: {
  addLineToSelection: DiffLinesSlice['diffLinesActions']['addLineToSelection']
  clearSelectedLines: () => void
  containerRef: React.RefObject<HTMLElement | null>
  fileAnchor: DiffAnchor
  getfirstLineSelectionOnDrag: DiffLinesSlice['diffLines']['getfirstLineSelectionOnDrag']
  isSplitDiff: boolean
  leftLines: readonly ClientDiffLine[]
  rightLines?: readonly ClientDiffLine[]
  selectMultipleLines: DiffLinesSlice['diffLinesActions']['selectMultipleLines']
  disabled: boolean
}) {
  const prevGridNavigator = useRef<GridNavigator | undefined>(undefined)
  const gridNavigator = useMemo(
    () => new GridNavigator(fileAnchor, isSplitDiff, leftLines, rightLines),
    [fileAnchor, isSplitDiff, leftLines, rightLines],
  )

  useEffect(() => {
    // When a new navigator is created (e.g. context lines are injected or the diff view is toggled),
    // carry over the previously focused cell so the keyboard focus position is preserved. This runs
    // before useFocusNextCellAfterExpand's effect (declared below), which reads focusedGridCell.
    const previousNavigator = prevGridNavigator.current
    const previousFocusedCellId = previousNavigator?.focusedGridCell?.cellId
    if (previousNavigator && previousNavigator !== gridNavigator && previousFocusedCellId) {
      gridNavigator.focusFirstGridCell(previousFocusedCellId)
    }
    prevGridNavigator.current = gridNavigator
  }, [gridNavigator])

  // this is necessary to reset the last selected grid cell when the user mouse clicks to a new location in the grid
  useEffect(() => {
    const containerElement = containerRef.current
    const handleMouseDown = () => {
      gridNavigator.clearLastSelectedCell()
    }
    containerElement?.addEventListener('mousedown', handleMouseDown)
    return () => containerElement?.removeEventListener('mousedown', handleMouseDown)
  }, [containerRef, gridNavigator])

  // eslint-disable-next-line react-hooks/refs
  useFocusNextCellAfterExpand(fileAnchor, leftLines, gridNavigator, containerRef, prevGridNavigator.current, rightLines)
  useSyncedGridFocus(containerRef, gridNavigator)

  const getNextFocusable = useCallback(
    (_direction: Direction, from: Element | undefined, event: KeyboardEvent) => {
      const currentCell = from as HTMLElement
      const focusedGridCell = gridNavigator.getValidSelectableRowCell()

      // Handles CMD + A / CTRL + A key presses to select all difflines in a diff grid
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if ((event.metaKey || event.ctrlKey) && event.code === 'KeyA') {
        const firstCodeCell = gridNavigator.getFirstCodeCell()
        const lastCodeCell = gridNavigator.getLastCodeCell()

        // Avoid setting diff row range if we do not have valid Grid Cells
        if (!firstCodeCell || !lastCodeCell) return currentCell

        if (firstCodeCell.lineKey && firstCodeCell.lineNumber && firstCodeCell.orientation) {
          selectMultipleLines(
            {
              endLineKey: lastCodeCell.lineKey,
              endOrientation: lastCodeCell.orientation as DiffOrientation,
              startLineKey: firstCodeCell.lineKey,
              startOrientation: firstCodeCell.orientation,
              firstLineSelectionOnDrag: {
                lineKey: firstCodeCell.lineKey,
                lineNumber: firstCodeCell.lineNumber,
                orientation: firstCodeCell.orientation,
                shouldStartConversation: false,
              },
            },
            handleLineSelectionEffects,
          )
        }

        return currentCell
      }

      // Handles SHIFT + Arrow Down key presses to select focused diffline and diffline(s) below/after it.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.shiftKey && event.key === 'ArrowDown') {
        const nextSelectedCell = gridNavigator.getNextSelectableCell('ArrowDown')
        // Avoid setting diff row range if we do not have valid Grid Cells
        if (!nextSelectedCell || !focusedGridCell) return currentCell

        const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
        if (firstLineSelectionOnDrag) {
          addLineToSelection(
            {
              lineKey: nextSelectedCell.lineKey,
              orientation: nextSelectedCell.orientation as DiffOrientation,
            },
            handleLineSelectionEffects,
          )
        } else if (focusedGridCell.lineKey && focusedGridCell.lineNumber && focusedGridCell.orientation) {
          selectMultipleLines(
            {
              endLineKey: nextSelectedCell.lineKey,
              endOrientation: nextSelectedCell.orientation as DiffOrientation,
              startLineKey: focusedGridCell.lineKey,
              startOrientation: focusedGridCell.orientation,
              firstLineSelectionOnDrag: {
                lineKey: focusedGridCell.lineKey,
                lineNumber: focusedGridCell.lineNumber,
                orientation: focusedGridCell.orientation,
                shouldStartConversation: false,
              },
            },
            handleLineSelectionEffects,
          )
        }

        scrolElementIntoViewIfNeeded(nextSelectedCell.cellId)

        return currentCell
      }

      // Handles SHIFT + Arrow Up key presses to select focused diffline and diffline(s) above/before it.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.shiftKey && event.key === 'ArrowUp') {
        const nextSelectedCell = gridNavigator.getNextSelectableCell('ArrowUp')

        // Avoid setting diff row range if we do not have valid Grid Cells
        if (!nextSelectedCell || !focusedGridCell) return currentCell

        const firstLineSelectionOnDrag = getfirstLineSelectionOnDrag()
        if (firstLineSelectionOnDrag) {
          addLineToSelection(
            {
              lineKey: nextSelectedCell.lineKey,
              orientation: nextSelectedCell.orientation as DiffOrientation,
            },
            handleLineSelectionEffects,
          )
        } else if (focusedGridCell.lineKey && focusedGridCell.lineNumber && focusedGridCell.orientation) {
          selectMultipleLines(
            {
              endLineKey: focusedGridCell.lineKey,
              endOrientation: focusedGridCell.orientation,
              startLineKey: nextSelectedCell.lineKey,
              startOrientation: nextSelectedCell.orientation as DiffOrientation,
              firstLineSelectionOnDrag: {
                lineKey: focusedGridCell.lineKey,
                lineNumber: focusedGridCell.lineNumber,
                orientation: focusedGridCell.orientation,
                shouldStartConversation: false,
              },
            },
            handleLineSelectionEffects,
          )
        }

        scrolElementIntoViewIfNeeded(nextSelectedCell.cellId)

        return currentCell
      }

      // this is necessary in order to allow users to expand all hunks with the keyboard
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'ArrowLeft' && event.metaKey && event.shiftKey) {
        return currentCell
      }

      // Allow native browser behavior for cmd/alt+ArrowLeft to use browser history back
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if ((event.metaKey || event.altKey) && event.key === 'ArrowLeft') {
        goBack()
        return undefined
      }

      // Allow native browser behavior for cmd/alt+ArrowRight to use browser history back
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if ((event.metaKey || event.altKey) && event.key === 'ArrowRight') {
        goForward()
        return undefined
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const nextItem = gridNavigator.moveToNextItem(event.key, event.ctrlKey)
      const nextElement = nextItem && document.querySelector<HTMLElement>(`[data-grid-cell-id=${nextItem.cellId}]`)

      clearSelectedLines()
      gridNavigator.clearLastSelectedCell()

      // return undefined instead of null when `document.getElementById` can't find element
      return nextElement ?? undefined
    },
    [addLineToSelection, getfirstLineSelectionOnDrag, gridNavigator, selectMultipleLines, clearSelectedLines],
  )

  // Disabling these function calls until we determine how to better reset a grid cell focus position when users leaves the grid.
  // reset grid navigator's current cell back to the first cell when focus leaves the grid
  // const handleFocusOut = useCallback(() => gridNavigator.focusFirstGridCell(), [gridNavigator])
  // useFocusOutGridNavigationBehavior(containerRef, handleFocusOut)

  useFocusZone(
    {
      containerRef,
      bindKeys: GRID_NAV_KEYS,
      getNextFocusable,
      focusableElementFilter: element => element.tagName === 'TD' && element.getAttribute('role') === 'gridcell',
      focusInStrategy: 'previous',
      disabled,
    },
    [getNextFocusable],
  )

  return {gridNavigator}
}

function isElementInViewport(element: Element | undefined | null) {
  if (!ssrSafeDocument || !ssrSafeWindow) return false
  if (!element) return false
  // don't love doing some bounding clinet rect stuff, but it only happens when a user is doing keyboard shift navigation,
  // so fairly rare
  const rect = element.getBoundingClientRect()
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (ssrSafeWindow.innerHeight || ssrSafeDocument.documentElement.clientHeight) &&
    rect.right <= (ssrSafeWindow.innerWidth || ssrSafeDocument.documentElement.clientWidth)
  )
}

function scrolElementIntoViewIfNeeded(elementId: string | undefined) {
  // check if the next selected cell id is currently in the viewport, and if not, scroll it into view
  const elementToCheckFor = ssrSafeDocument?.querySelector<HTMLElement>(`[data-grid-cell-id=${elementId}]`)
  if (!isElementInViewport(elementToCheckFor)) {
    elementToCheckFor?.scrollIntoView({block: 'nearest', inline: 'nearest'})
  }
}
